import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client/react';
import cn from 'clsx';
import evalStyles from './style.module.scss';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import {
    ExtendedCompleteRiskFields,
    ExtendedCompleteRiskFieldsEvaluationHistory,
} from '@pages-clean/risk-assessment/risk-log/constants';
import { EvaluationSelectorController } from '@pages-clean/risk-assessment/risk-log/dialogs/evaluation/evaluation-selector';
import styles from '@pages-clean/risk-assessment/risk-log/dialogs/style.module.scss';
import { NullableStudy } from '@contexts/local-state-context';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { getRiskEvaluationModels } from '@queries/generated/legacy-risk-assessment.generated';
import { saveRiskEvaluation, saveRiskEvaluationVariables } from '@queries/generated/risk-assessment.generated';
import {
    GET_COMPLETE_RISKS_RISK_LOG,
    GET_RISK_EVALUATION_MODELS,
    SAVE_RISK_EVALUATION,
} from '@queries/risk-assessment';

interface EvaluationDialogProps {
    addEvaluationAction: boolean;
    risk: ExtendedCompleteRiskFields;
    evaluation: ExtendedCompleteRiskFieldsEvaluationHistory | undefined;
    evaluationCount: number;
    study: NullableStudy;
    onClose: () => void;
    onSubmit: () => void;
}

type EvaluationFormInputs = {
    justificationForChange: string;
    rationale: string;
    evaluation: {
        likelihood?: number;
        impact?: number;
        detectability?: number;
    };
};

const COMPONENT_NAME = 'add-edit-evaluation';

const AddEditEvaluationDialog = ({
    risk,
    evaluation,
    study,
    evaluationCount,
    onSubmit,
    onClose,
    addEvaluationAction,
}: EvaluationDialogProps) => {
    const {
        t,
        i18n: { t: tRoot },
        ready,
    } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.addEditEvaluation' });

    const { data: riskEvaluationModelData, loading: loadingRiskEvaluationModelData } =
        useQuery<getRiskEvaluationModels>(GET_RISK_EVALUATION_MODELS);

    const [saveEvaluation] = useMutation<saveRiskEvaluation, saveRiskEvaluationVariables>(SAVE_RISK_EVALUATION);
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const evaluationModelId = evaluation ? evaluation?.evaluationModelId : study?.evaluationModelId;

    const evaluationModel = riskEvaluationModelData?.riskEvaluationModels?.find(
        (re) => re !== null && re.id === evaluationModelId,
    );

    const rationale = evaluation?.rationale ?? '';
    const justIficationForChangeDefaultText =
        evaluationCount === 0 ? t('originalEvaluation') : addEvaluationAction ? '' : (evaluation?.changeReason ?? '');

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid, isDirty, isSubmitting, dirtyFields },
    } = useForm<EvaluationFormInputs>({
        mode: 'all',
        defaultValues: useMemo(() => {
            return {
                justificationForChange: justIficationForChangeDefaultText,
                rationale: rationale ?? '',
                evaluation: {
                    likelihood: evaluation?.likelihood ?? undefined,
                    impact: evaluation?.impact ?? undefined,
                    detectability: evaluationModel?.includeDetectability ? (evaluation?.detectability ?? undefined) : 0,
                },
            };
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/exhaustive-deps
        }, [evaluationModel]),
    });

    useEffect(() => {
        if (evaluationModel)
            reset({
                justificationForChange: justIficationForChangeDefaultText,
                rationale: rationale ?? '',
                evaluation: {
                    likelihood: evaluation?.likelihood ?? undefined,
                    impact: evaluation?.impact ?? undefined,
                    detectability: evaluationModel?.includeDetectability ? (evaluation?.detectability ?? undefined) : 0,
                },
            });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [evaluationModel]);

    let likelyhoodDescriptions = '';
    let impactDescriptions = '';
    let detectabilityDescriptions = '';
    if (!loadingRiskEvaluationModelData && evaluationModelId && riskEvaluationModelData?.riskEvaluationModels?.length) {
        if (evaluationModel) {
            likelyhoodDescriptions = evaluationModel.likelihood
                ? evaluationModel?.likelihood
                      .filter((curEval) => curEval?.enabled)
                      .reduce((sum: string, elem) => `${sum}${elem?.value}-${elem?.description} <br>`, '')
                : '';
            impactDescriptions = evaluationModel.impact
                ? evaluationModel?.impact
                      .filter((curEval) => curEval?.enabled)
                      .reduce((sum: string, elem) => `${sum}${elem?.value}-${elem?.description} <br>`, '')
                : '';
            detectabilityDescriptions =
                evaluationModel?.includeDetectability && evaluationModel.detectability
                    ? evaluationModel?.detectability
                          .filter((curEval) => curEval?.enabled)
                          .reduce((sum: string, elem) => `${sum}${elem?.value}-${elem?.description} <br>`, '')
                    : '';
        }
    }

    const onSaveEvaluation = async (data: EvaluationFormInputs) => {
        saveEvaluation({
            variables: {
                riskId: risk.id,
                evaluation: {
                    id: !addEvaluationAction ? evaluation?.id : undefined,
                    evaluationModelId,
                    changeReason: data.justificationForChange,
                    rationale: data.rationale,
                    ...data.evaluation,
                },
            },
            refetchQueries: [
                {
                    query: GET_COMPLETE_RISKS_RISK_LOG,
                    variables: {
                        studyId: study?.id,
                    },
                },
            ],
        })
            .then((response) => {
                if (!response?.data?.saveRiskEvaluation) {
                    ShowError(tRoot('errorMessage'));
                    return;
                }

                setComponentHasChanges(COMPONENT_NAME, false);
                ShowSuccess(tRoot('actionsSuccessfulToast'));
                onSubmit();
            })
            .catch(() => {
                ShowError(tRoot('errorMessage'));
            });
    };

    const onCloseHandler = async () => {
        if (componentHasChanges(COMPONENT_NAME) && Object.keys(dirtyFields).length > 0) {
            const confirmed = await confirmDiscardChanges(`${COMPONENT_NAME}-unsaved-dialog`);
            if (!confirmed) {
                return;
            }
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        onClose();
    };

    const dialogTitle = addEvaluationAction
        ? `${tRoot('common:add')} ${t('title')}`
        : `${tRoot('common:edit')} ${t('title')}`;
    const primaryBtnLabel = addEvaluationAction ? tRoot('common:add') : tRoot('common:update');
    const createdBy = `${stringDateWithoutTimeFromUtc(evaluation?.created, 'DD-MMM-YYYY')} by ${evaluation?.createdBy}`;
    const updatedBy = `${stringDateWithoutTimeFromUtc(evaluation?.updated, 'DD-MMM-YYYY')} by ${evaluation?.updatedBy}`;

    if (loadingRiskEvaluationModelData || !riskEvaluationModelData?.riskEvaluationModels || !ready) return;

    if (!loadingRiskEvaluationModelData && !evaluationModel)
        return (
            <DraggableDialog
                dataTestId="add-evaluation-warning"
                onClose={onClose}
                title={dialogTitle}
                initialWidth={530}
                initialHeight={280}
                height={null}
                primaryBtnLabel={t('buttons.restoreAction')}
                secondaryBtnLabel={tRoot('close')}
            >
                <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                    <InfoField type="Warning" message={t('warning')} />
                </div>
            </DraggableDialog>
        );

    const likelihoodOptions =
        (evaluationModel?.likelihood && evaluationModel?.likelihood.filter((like) => like !== null && like.enabled)) ??
        [];
    const impactOptions =
        (evaluationModel?.impact && evaluationModel?.impact.filter((like) => like !== null && like.enabled)) ?? [];

    const detectabilityOptions = evaluationModel?.includeDetectability
        ? ((evaluationModel?.detectability &&
              evaluationModel?.detectability.filter((like) => like !== null && like.enabled)) ??
          [])
        : [];

    const maxValuesCount = Math.max(likelihoodOptions.length, impactOptions.length, detectabilityOptions.length);
    const valuesHeight = 35 * maxValuesCount; // 35 = 20 pixels for option height + 15px bottom margin
    const targetHeight = (addEvaluationAction ? 434 : 480) + valuesHeight;

    return (
        <DraggableDialog
            className={cn(styles.kWindowContent, 'add-risk-evaluation-dialog')}
            dataTestId="add-edit-evaluation-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSaveEvaluation)()}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            initialHeight={window.innerHeight >= targetHeight ? targetHeight : window.innerHeight}
            initialWidth={evaluationModel?.includeDetectability ? 520 : 400}
            createdUpdatedInfo={
                evaluation && !addEvaluationAction ? (
                    <CreatedUpdatedInfo created={createdBy} updated={updatedBy} />
                ) : undefined
            }
            createdUpdatedBackgroundColour="gray"
            bodyPadding="none"
        >
            <div className={cn('k-w-full k-h-full k-d-flex-col k-pt-xs')}>
                <div className={cn('k-d-flex k-w-full k-py-xs k-pl-md', evalStyles.evaluationModelContainer)}>
                    <div>
                        <span>
                            <span className="k-font-medium">{t('evaluationModel')}: </span>
                            {evaluationModel?.name}
                        </span>
                    </div>
                </div>
                <form id="add-edit-evaluation" className="k-w-full k-h-full k-pl-md k-pr-md k-pb-md k-pt-sm">
                    <div className="k-d-flex k-flex-auto k-justify-content-center">
                        <TextAreaController
                            className={styles.textAreaOverrides}
                            maxLength={150}
                            control={control}
                            name="justificationForChange"
                            data-testid="risk-evaluation-field-justificationForChange"
                            label={t('fields.justificationForChange')}
                            required
                        />
                    </div>
                    <div className="k-d-flex k-flex-auto k-justify-content-center">
                        <TextAreaController
                            className={styles.textAreaOverrides}
                            maxLength={2000}
                            control={control}
                            name="rationale"
                            data-testid="risk-evaluation-field-rationale"
                            label={t('fields.rationale')}
                        />
                    </div>
                    <div className="button-wrapper">
                        {evaluationModel?.likelihood && (
                            <EvaluationSelectorController
                                evaluationModel={{
                                    key: 'likelihood',
                                    label: t('fields.likelihood'),
                                    tooltip: likelyhoodDescriptions,
                                    evaluationsOptions: likelihoodOptions,
                                }}
                                control={control}
                                name="evaluation.likelihood"
                                data-testid="risk-evaluation-field-likelihood"
                                required
                            />
                        )}
                        {evaluationModel?.impact && (
                            <EvaluationSelectorController
                                evaluationModel={{
                                    key: 'impact',
                                    label: t('fields.impact'),
                                    tooltip: impactDescriptions,
                                    evaluationsOptions: impactOptions,
                                }}
                                control={control}
                                name="evaluation.impact"
                                data-testid="risk-evaluation-field-impact"
                                required
                            />
                        )}
                        {evaluationModel?.includeDetectability && evaluationModel?.detectability && (
                            <EvaluationSelectorController
                                evaluationModel={{
                                    key: 'detectability',
                                    label: t('fields.detectability'),
                                    tooltip: detectabilityDescriptions,
                                    evaluationsOptions: detectabilityOptions,
                                }}
                                control={control}
                                name="evaluation.detectability"
                                data-testid="risk-evaluation-field-detectability"
                                required
                            />
                        )}
                    </div>
                </form>
            </div>
        </DraggableDialog>
    );
};

export default AddEditEvaluationDialog;
